import { JSX, MouseEvent, MouseEventHandler, useState } from "react";
import { QueryChanger, usePageQuery } from "../../shared/hooks/usePageQuery";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { ErrorView } from "../../components/error-view/ErrorView";
import { LoadingView } from "../../components/loading-view/LoadingView";
import FunctionalList, {
  TableData,
} from "../../components/GenericTable/FunctionalList";
import { Organization } from "../../../core/models/entities/Organization";
import { PageQuery } from "../../../utils/models/pageQuery";
import { useNavigate } from "react-router-dom";
import { ColumnSettings } from "../../components/GenericTable/SubComponents/ListFrame";
import { StrucRow } from "../../components/StructureElements/StrucRow";
import { StrucCol } from "../../components/StructureElements/StrucCol";
import { nameOf } from "../../../utils/functions/ReflectionUtil";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { formatDate } from "../../../utils/functions/DateUtil";
import { vendorColumnsConfig } from "./const/vendorColumnsConfig";
import { Tooltip } from "@mui/material";
import IconButton from "../../components/LogoButton/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { SortDirection } from "../../components/GenericTable/Models/sortDirection";
import { useTranslation } from "react-i18next";

// Page size options offered to admins on the vendor list. The first entry is
// also the default applied on initial load when the URL has no pageSize.
const VENDOR_PAGE_SIZES = [24, 48, 96];
const DEFAULT_VENDOR_PAGE_SIZE = VENDOR_PAGE_SIZES[0];

export function VendorListScreen(): JSX.Element {
  const [rawQuery, queryChanger] = usePageQuery();

  // Apply vendor-specific defaults (sort A-Z by name, page size 24) directly
  // during render so they're present on the very first fetch. Setting these
  // via useEffect causes a race with usePageQuery's URL<->state sync that
  // wipes the defaults back out and leaves the table empty until the user
  // triggers another query change.
  const query: PageQuery = {
    ...rawQuery,
    sortBy: rawQuery.sortBy?.field
      ? rawQuery.sortBy
      : { field: "name", sortDirection: SortDirection.ASC },
    page: {
      ...rawQuery.page,
      size: VENDOR_PAGE_SIZES.includes(rawQuery.page?.size)
        ? rawQuery.page.size
        : DEFAULT_VENDOR_PAGE_SIZE,
    },
  };

  const { state, dataTransition } = usePagedComponentStateMachine();
  const { organizationRepository } = useRepositoriesContext();

  const [lastPage, setLastPage] = useState<number>(1);

  const {
    data: vendors,
    isSuccess: isManufacturersSuccess,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = organizationRepository.useAllVendors(query);

  if (vendors?.pageCount && vendors.pageCount !== lastPage) {
    setLastPage(vendors?.pageCount ?? 1);
  }

  dataTransition(isManufacturersLoading, isManufacturersSuccess, isManufacturersError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <VendorListWrapper
      organizations={vendors?.results}
      isPageLoading={state === PagedComponentState.PageLoading}
      query={query}
      queryChanger={queryChanger}
      lastPage={lastPage}
    />
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    <LoadingView />
  );
}

interface WrapperProps {
  organizations?: Organization[];
  isPageLoading: boolean;
  query: PageQuery;
  queryChanger: QueryChanger;
  lastPage: number;
}

export function VendorListWrapper({
  organizations,
  isPageLoading,
  query,
  queryChanger,
  lastPage,
}: WrapperProps) {
  const vm = useFunctionalListVendorViewModel(
    isPageLoading,
    query,
    queryChanger,
    lastPage,
    organizations
  );
  return <FunctionalList<Organization> {...vm} />;
}

export function useFunctionalListVendorViewModel(
  isLoading: boolean,
  query: PageQuery,
  queryChanger: QueryChanger,
  lastPage: number,
  organizations?: Organization[]
): TableData<Organization> {
  const navigate = useNavigate();
  const { currency } = useProfileContext();
  const { t } = useTranslation();

  function handleRowClick(organization: Organization) {
    navigate(`/vendor/${organization.id}`);
  }

  const columnLabelKeys: Record<string, string> = {
    [nameOf<Organization>((x) => x.name)]: "orgScreens.columnVendor",
    [nameOf<Organization>((x) => x.contactPerson)]:
      "orgScreens.columnContactPerson",
    [nameOf<Organization>((x) => x.email)]: "orgScreens.columnEmail",
    [nameOf<Organization>((x) => x.phone)]: "orgScreens.columnPhone",
    [nameOf<Organization>((x) => x.lastOrder)]:
      "orgScreens.columnLastOrderUpper",
    ["childCount"]: "orgScreens.columnLinkedOrgs",
    actions: "orgScreens.columnActionUpper",
  };

  const columns = vendorColumnsConfig.map((column) => ({
    ...column,
    displayValue: columnLabelKeys[column.value]
      ? t(columnLabelKeys[column.value])
      : column.displayValue,
  }));

  return {
    data: organizations ?? [],
    query: query,
    lastPage: lastPage,
    isLoading: isLoading,
    tableTitle: t("vendors"),
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    multiSelect: false,
    columns: columns,
    pageSizes: VENDOR_PAGE_SIZES,
    onNewSort: queryChanger.handleSortByField,
    onPageSizeChange: queryChanger.handlePageSizeChange,
    onNewPage: queryChanger.handleNewPage,
    renderRow: (columns, data) => (
      <VendorListItemComponent
        organization={data}
        currency={currency}
        columns={columns}
      />
    ),
    onRowClick: handleRowClick,
    getKey: (d) => d.id,
  };
}

export interface VendorListItemProps {
  organization: Organization;
  columns: ColumnSettings;
  currency: string;
}

export function VendorListItemComponent({
  organization,
  columns,
  currency,
}: VendorListItemProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  function handleEditRow(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    navigate(`/vendor/${organization.id}/edit`);
  }

  return (
    <StrucRow className={"py-4"}>
      <StrucCol size={columns[nameOf<Organization>((org) => org.name)].weight}>
        <Tooltip title={organization.name} placement={"top-start"}>
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
          >
            {organization.name}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol
        size={columns[nameOf<Organization>((org) => org.contactPerson)].weight}
      >
        <Tooltip title={organization.contactPerson} placement={"top-start"}>
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
          >
            {organization.contactPerson}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol size={columns[nameOf<Organization>((org) => org.email)].weight}>
        <Tooltip title={organization.email} placement={"top-start"}>
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
          >
            {organization.email}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol size={columns[nameOf<Organization>((org) => org.phone)].weight}>
        {
          // @ts-ignore
          formatPhoneNumberIntl(organization.phone)
        }
      </StrucCol>
      {/* <StrucCol
        size={
          columns[nameOf<Organization>((org) => org.totalOrderValue)].weight
        }
      >
        {currency}{" "}
        {organization.totalOrderValue
          ? organization.totalOrderValue.toFixed(2)
          : "0.00"}
      </StrucCol> */}
      <StrucCol
        size={columns[nameOf<Organization>((org) => org.lastOrder)].weight}
      >
        {organization.lastOrder ? formatDate(organization.lastOrder) : "-"}
      </StrucCol>
      <StrucCol size={columns["childCount"].weight}>
        {organization.childCount ?? 0}
      </StrucCol>
      <StrucCol size={columns["actions"].weight} className={"my-[-0.5rem]"}>
        <IconButton
          icon={<FontAwesomeIcon icon={faPenToSquare} />}
          onClick={handleEditRow}
        >
          {t("edit")}
        </IconButton>
      </StrucCol>
    </StrucRow>
  );
}
